import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  ConfirmDialog,
  InfoBox,
  Input,
  Label,
  Toast,
  cn,
  kstDateStr,
  kstTimeStr,
} from '@biznomi/ui-react'
import { manualReasonLabel } from '@biznomi/types'
import { useOrg } from '@/lib/useOrg'
import {
  submitManualInput,
  getMyAttendance,
  MANUAL_REASONS,
  ABSENCE_REASONS,
  type ManualInput,
  type AttendanceRecord,
} from '@/lib/api/attendance'

/**
 * 수기 등록 / 부재 등록 — 탭으로 나눈다 (명세 §16-D16).
 *
 * 둘은 성격이 다르다.
 *   수기 = 출근은 했는데 태깅을 못 함 → 시각이 있다
 *   부재 = 애초에 출근하지 않음      → 시각이 없다 (종일)
 * 한 화면에 섞으면 직원이 휴가를 "출근 09:00"으로 등록하게 된다.
 *
 * ⚠️ 날짜·시간은 네이티브 input[type=date/time] 을 쓴다. 커스텀 피커로 바꾸지 말 것 —
 *    iOS 에서 오버레이·2열 레이아웃·select 관련 함정이 확인됐고, 24h 강제는 포기했다.
 *    실 iPhone 캡처가 prod 게이트다.
 */

type Tab = 'manual' | 'absence'

export default function ManualEntry() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: org } = useOrg()

  const [tab, setTab] = useState<Tab>('manual')
  const [type, setType] = useState<'CHECKIN' | 'CHECKOUT'>('CHECKIN')
  /**
   * 2026-08-21 — 정정인지 추가인지 직원이 직접 고른다.
   *
   * 안 고르게 두면 시스템이 "같은 날 같은 종류" 를 보고 추측해야 하는데, 그러면
   * 하루에 두 번 출근한 사람(재출근)을 정정으로 오인한다. 실제로 그 오인 때문에
   * 아침 태그가 화면에서 사라진 적이 있다(2026-07-15 supersede 폐기).
   * 사람이 아는 걸 사람에게 묻는다.
   */
  const [mode, setMode] = useState<'add' | 'correct'>('add')
  const [correctsId, setCorrectsId] = useState<string | null>(null)
  const [date, setDate] = useState(kstDateStr())
  const [time, setTime] = useState('')
  const [reason, setReason] = useState<string>(MANUAL_REASONS[0])
  const [note, setNote] = useState('')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [toast, setToast] = useState('')
  const [confirmSameDay, setConfirmSameDay] = useState<string | null>(null)

  const isAbsence = tab === 'absence'
  const isCorrecting = !isAbsence && mode === 'correct'

  /*
   * 정정 대상 후보 — 고른 날짜의 내 기록.
   * 정정 모드일 때만 부른다. 그냥 추가하는 사람에겐 필요 없는 요청이다.
   */
  const { data: dayRecords, isLoading: loadingDay } = useQuery({
    queryKey: ['attendance', 'range', date, date],
    queryFn: () => getMyAttendance({ from: date, to: date }),
    enabled: isCorrecting && !!date,
  })
  const candidates: AttendanceRecord[] = (dayRecords?.success ? dayRecords.data ?? [] : [])
    // 같은 종류만 / 부재는 정정 대상 아님 / 이미 정정 신청된 건 제외 / 반려된 수기 제외
    .filter((r) => r.type === type)
    .filter((r) => !r.supersededById)
    .filter((r) => r.manualStatus !== 'REJECTED')
  /*
   * 부재 사유는 조직이 고른 것만 보여준다 (§4 B #10).
   * 비어 있으면 전부 — 설정을 안 만진 조직이 갑자기 아무것도 못 고르면 안 된다.
   * 조직이 고른 값이 enum 에 없는 경우(사유가 나중에 제거된 경우)도 걸러낸다.
   */
  const allowed = org?.uiConfig?.absenceReasons ?? []
  const absenceChoices = allowed.length
    ? ABSENCE_REASONS.filter((r) => allowed.includes(r))
    : ABSENCE_REASONS
  const reasons = isAbsence
    ? (absenceChoices.length ? absenceChoices : ABSENCE_REASONS)
    : MANUAL_REASONS
  // 출퇴근 수기는 왜 태깅을 못 했는지 적어야 한다. 부재는 사유 자체가 설명이다.
  const noteRequired = !isAbsence

  function switchTab(next: Tab) {
    setTab(next)
    setReason(next === 'absence' ? ABSENCE_REASONS[0] : MANUAL_REASONS[0])
    setMode('add')
    setCorrectsId(null)
    setError('')
  }

  async function send(force: boolean) {
    setBusy(true)
    setError('')

    const payload: ManualInput = {
      // 부재도 CHECKIN 으로 보낸다 — 서버가 사유를 보고 ABSENCE 로 매핑한다
      type: isAbsence ? 'CHECKIN' : type,
      date,
      ...(isAbsence ? {} : { time }),
      reason,
      note: note.trim() || undefined,
      ...(force ? { force: true } : {}),
      ...(isCorrecting && correctsId ? { correctsId } : {}),
    }

    const res = await submitManualInput(payload)
    setBusy(false)

    if (res.success) {
      setConfirmSameDay(null)
      qc.invalidateQueries({ queryKey: ['attendance'] })
      setToast(
        isAbsence
          ? '부재가 등록되었습니다'
          : isCorrecting
            ? '정정 요청이 접수되었습니다'
            : '수기 등록이 접수되었습니다',
      )
      setTimeout(() => navigate('/records'), 900)
      return
    }

    // 같은 날 부재 중복 — 하드 차단이 아니라 되묻는 신호다.
    // 문구가 아니라 code 로 분기한다. 문구는 바뀔 수 있다.
    if (res.code === 'ABSENCE_SAME_DAY') {
      setConfirmSameDay(res.error ?? '같은 날짜에 부재가 이미 있습니다. 그래도 등록할까요?')
      return
    }
    setError(res.error ?? '등록에 실패했습니다')
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (noteRequired && !note.trim()) {
      setError('사유를 적어 주세요')
      return
    }
    if (!isAbsence && !time) {
      setError('시각을 입력해 주세요')
      return
    }
    if (isCorrecting && !correctsId) {
      setError('정정할 기록을 골라 주세요')
      return
    }
    void send(false)
  }

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold">직접 등록</h1>

      {/* 탭 */}
      <div role="tablist" className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
        {(
          [
            ['manual', '출퇴근 수기'],
            ['absence', '부재 등록'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => switchTab(key)}
            className={cn(
              'rounded-md py-2.5 text-sm font-semibold transition-colors',
              tab === key ? 'bg-white text-primary shadow-sm' : 'text-slate-500',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <InfoBox tone="info">
        {isAbsence
          ? '휴가·출장·교육처럼 출근하지 않은 날을 남깁니다. 시각은 없습니다.'
          : isCorrecting
            ? '이미 있는 기록의 시각을 바로잡습니다. 원래 기록은 지워지지 않고 "정정됨" 으로 함께 남습니다.'
            : '출근은 했는데 태깅을 못 한 경우입니다. 관리자 승인 후 기록에 반영됩니다.'}
      </InfoBox>

      <form onSubmit={submit} className="space-y-4">
        {/* 정정인가 추가인가 — 시스템이 추측하지 않고 직원이 고른다 */}
        {!isAbsence && (
          <div className="space-y-1.5">
            <Label>무엇을 하시나요</Label>
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  ['add', '새로 추가'],
                  ['correct', '기존 기록 정정'],
                ] as const
              ).map(([v, label]) => (
                <Button
                  key={v}
                  type="button"
                  variant={mode === v ? 'default' : 'secondary'}
                  onClick={() => {
                    setMode(v)
                    setCorrectsId(null)
                    setError('')
                  }}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {!isAbsence && (
          <div className="space-y-1.5">
            <Label>구분</Label>
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  ['CHECKIN', '출근'],
                  ['CHECKOUT', '퇴근'],
                ] as const
              ).map(([v, label]) => (
                <Button
                  key={v}
                  type="button"
                  variant={type === v ? 'default' : 'secondary'}
                  onClick={() => setType(v)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="date">날짜</Label>
          {/* 네이티브 input 유지 — iOS 함정 때문에 커스텀 피커로 바꾸지 않는다 */}
          <Input id="date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </div>

        {isCorrecting && (
          <div className="space-y-1.5">
            <Label>어느 기록을 고칠까요</Label>
            {loadingDay ? (
              <p className="py-2 text-sm text-slate-400">불러오는 중…</p>
            ) : candidates.length === 0 ? (
              <p className="rounded-lg bg-slate-50 px-3 py-3 text-sm text-slate-500">
                그 날짜에 정정할 {type === 'CHECKIN' ? '출근' : '퇴근'} 기록이 없습니다.
                날짜·구분을 확인하시거나 «새로 추가»를 쓰세요.
              </p>
            ) : (
              <ul className="space-y-2">
                {candidates.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setCorrectsId(r.id)
                        // 고른 기록의 시각을 미리 채워 준다 — 대개 분 단위만 고친다
                        setTime(kstTimeStr(r.timestamp))
                        setError('')
                      }}
                      className={cn(
                        'flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left transition-colors',
                        correctsId === r.id
                          ? 'border-primary bg-primary/10'
                          : 'border-slate-200 bg-white',
                      )}
                    >
                      <span className="text-sm text-slate-600">
                        {r.isManual ? '수기' : r.nfcSpot?.name ?? 'NFC'}
                      </span>
                      <span className="font-semibold tabular-nums">{kstTimeStr(r.timestamp)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {!isAbsence && (
          <div className="space-y-1.5">
            <Label htmlFor="time">{isCorrecting ? '고칠 시각' : '시각'}</Label>
            <Input id="time" type="time" value={time} onChange={(e) => setTime(e.target.value)} required />
          </div>
        )}

        <div className="space-y-1.5">
          <Label>사유</Label>
          <div className="flex flex-wrap gap-2">
            {reasons.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(r)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-sm transition-colors',
                  reason === r
                    ? 'border-primary bg-primary/10 font-semibold text-primary'
                    : 'border-slate-200 bg-white text-slate-600',
                )}
              >
                {manualReasonLabel(r)}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="note">
            메모 {noteRequired ? <span className="text-[var(--color-danger)]">*</span> : '(선택)'}
          </Label>
          <textarea
            id="note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
            rows={3}
            placeholder={isAbsence ? '필요하면 덧붙여 주세요' : '어떤 상황이었는지 적어 주세요'}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-base placeholder:text-slate-400 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
          <p className="text-right text-xs text-slate-400">{note.length}/200</p>
        </div>

        {error && (
          <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
            {error}
          </p>
        )}

        <Button type="submit" size="lg" className="w-full" loading={busy}>
          {isAbsence ? '부재 등록' : isCorrecting ? '정정 요청' : '수기 등록 요청'}
        </Button>
      </form>

      <ConfirmDialog
        open={confirmSameDay !== null}
        onOpenChange={(o) => !o && setConfirmSameDay(null)}
        title="이미 등록된 부재가 있습니다"
        message={confirmSameDay ?? ''}
        confirmText="그래도 등록"
        loading={busy}
        onConfirm={() => void send(true)}
      />

      {toast && <Toast message={toast} variant="success" onClose={() => setToast('')} />}
    </div>
  )
}
